import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts';
import type { RegisterCredentials } from '../../types/auth';

const RegisterForm: React.FC = () => {
  const { register, isLoading, error } = useAuth();
  const [credentials, setCredentials] = useState<RegisterCredentials>({
    email: '',
    password: '',
    confirmPassword: '',
    industry: 'financial_services',
    companyName: '',
    jobTitle: '',
    companySize: 'small',
    experienceLevel: 'mid',
  });
  const [validationError, setValidationError] = useState<string>('');

  // Override global body styles that prevent scrolling
  useEffect(() => {
    // Store original styles
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;
    
    // Apply styles that allow scrolling
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    
    // Cleanup function to restore original styles
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.height = originalHeight;
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    setValidationError('');
  };

  const validateForm = (): boolean => {
    if (credentials.password !== credentials.confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }
    if (credentials.password.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return false;
    }
    if (!credentials.companyName.trim()) {
      setValidationError('Company name is required');
      return false;
    }
    if (!credentials.jobTitle.trim()) {
      setValidationError('Job title is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      await register(credentials);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-lg mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-center">Create Account</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={credentials.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-purple"
              placeholder="Enter your email"
            />
          </div>

          {/* Password Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-purple"
                placeholder="Create a password"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={credentials.confirmPassword}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-purple"
                placeholder="Confirm your password"
              />
            </div>
          </div>

          {/* Industry */}
          <div>
            <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
              Industry
            </label>
            <select
              id="industry"
              name="industry"
              value={credentials.industry}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-purple"
            >
              <option value="financial_services">Financial Services</option>
              <option value="healthcare">Healthcare</option>
              <option value="sales">Sales & Marketing</option>
              <option value="real_estate">Real Estate</option>
              <option value="legal">Legal Services</option>
              <option value="hr">Human Resources</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Company Name & Company Size Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={credentials.companyName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-purple"
                placeholder="Enter your company name"
              />
            </div>

            <div>
              <label htmlFor="companySize" className="block text-sm font-medium text-gray-700 mb-1">
                Company Size
              </label>
              <select
                id="companySize"
                name="companySize"
                value={credentials.companySize}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-purple"
              >
                <option value="startup">Startup (1-10)</option>
                <option value="small">Small (11-50)</option>
                <option value="medium">Medium (51-200)</option>
                <option value="enterprise">Enterprise (200+)</option>
              </select>
            </div>
          </div>

          {/* Job Title & Experience Level Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                Job Title
              </label>
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                value={credentials.jobTitle}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-purple"
                placeholder="Enter your job title"
              />
            </div>

            <div>
              <label htmlFor="experienceLevel" className="block text-sm font-medium text-gray-700 mb-1">
                Experience Level
              </label>
              <select
                id="experienceLevel"
                name="experienceLevel"
                value={credentials.experienceLevel}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-purple"
              >
                <option value="junior">Junior (0-2 years)</option>
                <option value="mid">Mid-level (3-7 years)</option>
                <option value="senior">Senior (8-15 years)</option>
                <option value="executive">Executive (15+ years)</option>
              </select>
            </div>
          </div>

          {(error || validationError) && (
            <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm">
              {validationError || error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-accent-purple text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="text-center mt-4">
            <span className="text-gray-600 text-sm">
              Already have an account?{' '}
              <a href="/login" className="text-accent-purple hover:underline">
                Sign in
              </a>
            </span>
          </div>

          {/* Registration Success Message */}
          <div className="text-center mt-4 pb-8">
            <span className="text-gray-600 text-sm">
              After registration, check your email for a verification code, then{' '}
              <a href="/verify-token" className="text-accent-purple hover:underline">
                verify your account here
              </a>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm; 